<template>
  <div class="page-container">
    <div :class="['search-wrapper', { 'searched-top': searched }]">
      <div class="baidu-search-box">
        <el-input v-model="tid" placeholder="输入订单ID，开启深度搜索..." :size="searched ? 'default' : 'large'"
          @keyup.enter="searchOrder" class="baidu-input">
          <template #prefix>
            <el-icon class="search-icon">
              <Search />
            </el-icon>
          </template>
          <template #suffix>
            <div class="search-divider" v-if="!searched"></div>
          </template>
        </el-input>
        <el-button type="primary" :loading="loading" @click="searchOrder" class="baidu-button"
          :size="searched ? 'default' : 'large'">
          搜索订单
        </el-button>
      </div>
    </div>

    <transition name="content-fade">
      <div v-if="searched" class="main-content">
        <el-row :gutter="24" class="card-row">
          <el-col :span="12">
            <el-card class="styled-card fixed-height-card" shadow="hover">
              <template #header>
                <div class="card-title">
                  <div class="title-dot blue"></div>
                  <span>订单基础信息</span>
                </div>
              </template>
              <div class="order-details">
                <div class="detail-item">
                  <span class="label">订单ID</span>
                  <span class="value">{{ orderData.tidStr }}</span>
                </div>
                <div class="detail-item">
                  <span class="label">定制ID</span>
                  <el-tag effect="light" round class="cid-tag">{{ orderData.cid }}</el-tag>
                </div>
                <div class="detail-item">
                  <span class="label">预览 PDF</span>
                  <el-link v-if="orderData.pdfUrl" :href="orderData.pdfUrl" target="_blank" type="primary"
                    class="pdf-link">
                    查看预览 PDF <el-icon>
                      <Document />
                    </el-icon>
                  </el-link>
                  <span v-else class="value-null">未生成</span>
                </div>
                <div class="detail-item">
                  <span class="label">创建时间</span>
                  <span class="value">{{ orderData.created }}</span>
                </div>
              </div>
            </el-card>
          </el-col>

          <el-col :span="12">
            <el-card class="styled-card fixed-height-card" shadow="hover">
              <template #header>
                <div class="card-title">
                  <div class="title-dot orange"></div>
                  <span>RDS 快捷控制台</span>
                </div>
              </template>
              <div class="rds-content">
                <el-link href="https://console.cloud.tmall.com/resourceView#/aliyun/product?productCode=rds"
                  target="_blank" :underline="false" class="premium-link">
                  <el-icon>
                    <Monitor />
                  </el-icon>
                  <span>访问阿里云控制台</span>
                  <el-icon class="arrow-icon">
                    <ArrowRight />
                  </el-icon>
                </el-link>

                <div class="sql-wrapper">
                  <div class="sql-box mini-sql">
                    <code class="mono-code">{{ querySql || '等待生成...' }}</code>
                  </div>
                  <el-button class="copy-link-btn" type="primary" link icon="DocumentCopy"
                    @click="copySql(querySql)">复制查询</el-button>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-row :gutter="24" class="card-row mt-24">
          <el-col :span="12">
            <el-card class="styled-card dynamic-card" shadow="hover">
              <template #header>
                <div class="card-header-flex">
                  <div class="card-title">
                    <div class="title-dot purple"></div>
                    <span>定制信息输入</span>
                  </div>
                  <el-tag v-if="parsedJson" :type="isOldVersion ? 'danger' : 'success'" effect="dark" round
                    size="small">
                    {{ isOldVersion ? '旧版音频' : '新版音频' }}
                  </el-tag>
                </div>
              </template>

              <div v-if="audioUrlIn" class="audio-player-wrapper">
                <span class="audio-label">当前音频：</span>
                <audio controls :src="audioUrlIn" class="custom-audio"></audio>
              </div>

              <el-input v-model="customJson" type="textarea" :rows="10" placeholder="粘贴 custom_json 字段内容..."
                class="modern-textarea" />
            </el-card>
          </el-col>

          <el-col :span="12">
            <el-card class="styled-card dynamic-card" shadow="hover">
              <template #header>
                <div class="card-title">
                  <div class="title-dot green"></div>
                  <span>生成更新 SQL
                    <span v-if="parsedJson" class="target-hint">➜ 目标: {{ isOldVersion ? '新版' : '旧版' }}</span>
                  </span>
                </div>
              </template>

              <div v-if="parsedJson" class="sql-full-container">
                <div v-if="audioUrlOut" class="audio-player-wrapper out">
                  <span class="audio-label">转换后音频预览：</span>
                  <audio controls :src="audioUrlOut" class="custom-audio"></audio>
                </div>

                <div class="sql-box-scroll-wrapper">
                  <div class="sql-box dark-sql-theme">
                    <pre class="sql-content-text"><code>{{ targetVersionSql }}</code></pre>
                  </div>
                </div>

                <el-button type="primary" class="premium-copy-btn" icon="DocumentCopy"
                  @click="copySql(targetVersionSql)">
                  复制更新 SQL
                </el-button>
              </div>
              <div v-else class="empty-state">
                <el-empty description="等待输入 JSON" :image-size="60" />
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </transition>
  </div>
</template>

<script setup>
/* 逻辑部分保持之前一致，仅更新了 tidStr 解析 */
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Monitor, ArrowRight, DocumentCopy, Document } from '@element-plus/icons-vue'
import http from '@/utils/http'

const tid = ref('')
const loading = ref(false)
const searched = ref(false)
const customJson = ref('')
const parsedJson = ref(null)
const orderData = ref({ tidStr: '', cid: '', openId: '', created: '', pdfUrl: '' })

function parsePdfUrl(data) {
  try {
    const customObj = JSON.parse(data.customization)
    return customObj.items?.[0]?.greetingCardMemo || ''
  } catch (e) { return '' }
}

const querySql = computed(() => orderData.value.cid ? `SELECT id, \`custom_json\` FROM \`custom_order\` WHERE id = ${orderData.value.cid};` : '')
const isOldVersion = computed(() => parsedJson.value?.video?.includes('/prod/user/'))
const audioUrlIn = computed(() => parsedJson.value?.video || '')
const audioUrlOut = computed(() => {
  if (!parsedJson.value?.video) return ''
  let url = parsedJson.value.video
  return isOldVersion.value ? url.replace('/prod/user/', '/audio/temp/') : url.replace('/audio/temp/', '/prod/user/')
})
const targetVersionSql = computed(() => {
  if (!audioUrlOut.value) return ''
  return `UPDATE \`custom_order\` SET \`custom_json\` = JSON_SET(\`custom_json\`, '$.video', '${audioUrlOut.value}') WHERE id = ${orderData.value.cid || 0};`
})

watch(customJson, (val) => {
  if (!val) { parsedJson.value = null; return }
  try { parsedJson.value = JSON.parse(val) } catch (e) { parsedJson.value = null }
})

async function searchOrder() {
  if (!tid.value) { ElMessage.warning('请输入订单ID'); return }
  loading.value = true
  try {
    const res = await http.get(`/chanel-c2b-api-tb/api/admin/trade/syncTid?tid=${tid.value}`)
    if (res.success && res.data?.length > 0) {
      const raw = res.data[0]
      orderData.value = { tidStr: raw.tidStr, cid: raw.cid, openId: raw.openId, created: raw.created, pdfUrl: parsePdfUrl(raw) }
      searched.value = true
      ElMessage.success('同步成功')
    } else { ElMessage.error(res.data?.errorMsg || '未找到该订单') }
  } catch (err) { ElMessage.error('查询异常') }
  finally { loading.value = false }
}

function copySql(text) {
  if (!text) return
  navigator.clipboard.writeText(text).then(() => ElMessage.success('复制成功'))
}
</script>

<style scoped>
.page-container {
  padding: 40px 20px;
  min-height: 100vh;
  background-color: #fbfbfc;
  font-family: sans-serif;
}

/* --- 百度式极简搜索框样式 --- */
.search-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-wrapper:not(.searched-top) {
  height: 40vh;
}

.search-wrapper.searched-top {
  height: 60px;
  margin-bottom: 30px;
}

.baidu-search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 2px solid #c4c7ce;
  border-radius: 10px;
  /* 百度现在的圆角较小，如需全圆角可改为 50px */
  transition: all 0.2s;
  overflow: hidden;
  width: 600px;
}

.baidu-search-box:hover {
  border-color: #a7aab5;
}

.baidu-search-box:focus-within {
  border-color: #4e6ef2;
  box-shadow: 0 0 10px rgba(78, 110, 242, 0.1);
}

/* 调整搜索框高度 */
.search-wrapper:not(.searched-top) .baidu-search-box {
  width: 650px;
  height: 50px;
  border-radius: 12px;
}

.search-wrapper.searched-top .baidu-search-box {
  width: 500px;
  height: 40px;
  border-radius: 8px;
}

:deep(.baidu-input .el-input__wrapper) {
  box-shadow: none !important;
  background: transparent;
  font-size: 16px;
}

.baidu-button {
  border: none !important;
  border-radius: 0 !important;
  background-color: #4e6ef2 !important;
  /* 百度蓝 */
  color: #fff !important;
  font-weight: bold;
  height: 100% !important;
  padding: 0 30px !important;
  font-size: 16px;
}

.baidu-button:hover {
  background-color: #4662d9 !important;
}

/* 搜索条内的小分割线 */
.search-divider {
  width: 1px;
  height: 20px;
  background-color: #eee;
  margin-right: 10px;
}

/* --- 其余卡片样式 --- */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
}

.styled-card {
  border-radius: 12px;
  border: 1px solid #ebeef5;
}

.fixed-height-card {
  height: 260px;
}

.dynamic-card {
  min-height: 520px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #333;
}

.title-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.blue {
  background: #4e6ef2;
}

.orange {
  background: #f59e0b;
}

.purple {
  background: #8b5cf6;
}

.green {
  background: #10b981;
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #f8f9fa;
}

.label {
  color: #666;
  font-size: 14px;
}

.value {
  color: #000;
  font-weight: 600;
  font-size: 14px;
}

.mono-code {
  font-family: monospace;
  color: #4e6ef2;
  background: #f0f3ff;
  padding: 2px 6px;
  border-radius: 4px;
}

/* SQL & 播放器 */
.sql-box {
  padding: 15px;
  border-radius: 8px;
}

.mini-sql {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
}

.dark-sql-theme {
  background: #1e293b;
  color: #38bdf8;
  border: 1px solid #334155;
}

.sql-content-text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
}

.audio-player-wrapper {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #eee;
}

.audio-player-wrapper.out {
  background: #f0fdf4;
  border-color: #dcfce7;
}

.custom-audio {
  width: 100%;
  height: 32px;
}

.premium-link {
  background: #f8f9fa;
  padding: 6px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  width: fit-content;
  font-size: 13px;
}

.premium-copy-btn {
  margin-top: 15px;
  width: 100%;
  height: 40px;
  font-weight: bold;
  border-radius: 8px !important;
}

.modern-textarea :deep(.el-textarea__inner) {
  border-radius: 8px;
  background-color: #fafafa;
  font-family: monospace;
}

.mt-24 {
  margin-top: 24px;
}

.card-header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>